/**
 * MALA — Floating product video.
 *
 * A muted corner player that morphs into an immersive, sound-on view with a
 * buy tray underneath. Both states share ONE <video> element, so expanding
 * never re-buffers or restarts playback.
 *
 * The morph is a FLIP: measure the frame before and after the layout switch,
 * then animate only `transform` (plus a scale-compensated `border-radius`).
 * Mini and expanded frames are locked to the same aspect ratio, which makes
 * the scale uniform — no distortion, and nothing but compositor work.
 *
 * Add to cart delegates to the real product form via the same "puppet click"
 * the theme's own sticky bar uses, so variant, quantity, validation and the
 * cart drawer all behave exactly as they do on the page.
 */

const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class MalaFloatingVideo extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {Animation | null} */
  #morph = null;

  /** @type {MutationObserver | null} */
  #formObserver = null;

  /** @type {MutationObserver | null} */
  #stickyObserver = null;

  /** @type {number | null} */
  #revealTimer = null;

  /** @type {number | null} */
  #revealFallbackTimer = null;

  /** @type {number | null} */
  #resizeFrame = null;

  /** @type {HTMLButtonElement | null} */
  #realButton = null;

  #scrollLocked = false;
  #previousOverflow = '';
  #pausedByShopper = false;
  #sourceReady = false;
  #loopsPlayed = 0;
  #triedMp4Fallback = false;

  connectedCallback() {
    // The widget is authored inside a section, but `position: fixed` breaks
    // under a transformed or clipping ancestor — and this theme puts a
    // view-transition-name on <main>. Reparent to <body> once; that move
    // re-runs this callback with the element already in place.
    if (this.parentElement !== document.body) {
      for (const other of document.querySelectorAll('mala-floating-video')) {
        if (other !== this && other.parentElement === document.body) other.remove();
      }
      document.body.appendChild(this);
      return;
    }

    this.#init();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  // ------------------------------------------------------------------ setup

  #init() {
    if (this.#abort) return;

    this.frame = this.querySelector('[data-mfv-frame]');
    this.video = this.querySelector('[data-mfv-video]');
    this.tray = this.querySelector('[data-mfv-tray]');
    this.atcButton = this.querySelector('[data-mfv-atc]');
    this.closeButton = this.querySelector('[data-mfv-close]');
    this.dismissButton = this.querySelector('[data-mfv-dismiss]');
    this.chip = this.querySelector('[data-mfv-chip]');
    this.soundButton = this.querySelector('[data-mfv-sound]');
    this.progressBar = this.querySelector('[data-mfv-progress]');

    if (!this.frame || !this.video) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.querySelector('[data-mfv-surface]')?.addEventListener('click', this.#handleSurface, { signal });
    this.querySelector('[data-mfv-backdrop]')?.addEventListener('click', () => this.close(), { signal });
    this.querySelector('[data-mfv-dismiss]')?.addEventListener('click', this.#dismiss, { signal });
    this.closeButton?.addEventListener('click', () => this.close(), { signal });
    this.soundButton?.addEventListener('click', this.#toggleSound, { signal });
    this.atcButton?.addEventListener('click', this.#addToCart, { signal });

    this.video.addEventListener('play', this.#syncPlayState, { signal });
    this.video.addEventListener('pause', this.#syncPlayState, { signal });
    this.video.addEventListener('timeupdate', this.#syncProgress, { signal });
    this.video.addEventListener('seeked', this.#countLoop, { signal });
    this.video.addEventListener('error', this.#handleMediaError, { signal });

    window.addEventListener('resize', this.#queueViewportSync, { signal });
    document.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibility, { signal });

    // The editor removes the *section*, but this node now lives on <body>.
    document.addEventListener('shopify:section:unload', this.#handleSectionUnload, { signal });

    this.#watchProductForm();
    this.#watchStickyBar();
    this.#syncViewport();
    this.#syncSoundIcon();
    this.#scheduleReveal();
  }

  #teardown() {
    this.#abort?.abort();
    this.#abort = null;
    this.#morph?.cancel();
    this.#morph = null;
    this.#formObserver?.disconnect();
    this.#formObserver = null;
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
    if (this.#revealTimer) window.clearTimeout(this.#revealTimer);
    if (this.#revealFallbackTimer) window.clearTimeout(this.#revealFallbackTimer);
    if (this.#resizeFrame) window.cancelAnimationFrame(this.#resizeFrame);
    this.#unloadVideo();
    this.#unlockScroll();
  }

  #handleSectionUnload = (event) => {
    if (event.detail?.sectionId && event.detail.sectionId === this.dataset.sectionId) this.remove();
  };

  // --------------------------------------------------------------- reveal

  get #dismissKey() {
    return `mala-fv-dismissed:${this.dataset.productId}`;
  }

  #scheduleReveal() {
    const designMode = Boolean(window.Shopify?.designMode);

    if (!designMode && this.#readDismissed()) {
      this.#unloadVideo();
      this.remove();
      return;
    }

    if (designMode) {
      this.#reveal();
      this.#play();
      return;
    }

    /*
      The order matters, and it is deliberate:
        1. wait for the page to finish loading completely,
        2. stay off the network for the configured gap,
        3. only then fetch video,
        4. and only slide the player in once frames are actually on screen.
      The shopper therefore never sees an empty box or a frozen thumbnail
      waiting to start, and nothing here competes with the product page.
    */
    this.#afterPageLoad(() => {
      this.#revealTimer = window.setTimeout(() => {
        if (!this.#autoplayAllowed()) {
          // Data saver: show the thumbnail and let the shopper choose.
          this.#showPoster();
          this.#reveal();
          return;
        }
        this.#playThenReveal();
      }, Number(this.dataset.delay || 0));
    });
  }

  #reveal() {
    if (this.#revealFallbackTimer) window.clearTimeout(this.#revealFallbackTimer);
    this.#revealFallbackTimer = null;
    this.dataset.ready = 'true';
    // Chat widgets and the sticky bar mount late — re-measure now that the
    // player is on screen and everything around it has settled.
    this.#syncViewport();
  }

  /** The poster is lazy too — attach it only when a still frame is needed. */
  #showPoster() {
    const { poster } = this.video.dataset;
    if (poster && !this.video.getAttribute('poster')) this.video.setAttribute('poster', poster);
  }

  #playThenReveal() {
    this.video.addEventListener('playing', () => this.#reveal(), {
      once: true,
      signal: this.#abort?.signal,
    });

    // A stalled network would otherwise mean the widget never appears at all.
    this.#revealFallbackTimer = window.setTimeout(() => {
      this.#showPoster();
      this.#reveal();
    }, 8000);

    this.#play();
  }

  #readDismissed() {
    try {
      return window.sessionStorage.getItem(this.#dismissKey) === '1';
    } catch {
      return false;
    }
  }

  #dismiss = () => {
    try {
      window.sessionStorage.setItem(this.#dismissKey, '1');
    } catch {
      /* Private mode — dismissal simply won't persist. */
    }
    this.#unloadVideo();
    this.remove();
  };

  // ------------------------------------------------------------ playback

  /**
   * A corner player is not worth a metered megabyte. On an explicit data-saver
   * setting or a very slow connection the poster stands in until the shopper
   * taps — expanding always plays regardless.
   */
  #afterPageLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    window.addEventListener('load', callback, { once: true, signal: this.#abort?.signal });
  }

  /**
   * Picks the lowest-bandwidth rendition out of an HLS master playlist. Native
   * HLS otherwise ramps up to the 2.5 Mbps stream on a fast connection, which
   * is absurd for a thumbnail-sized player — and expensive on mobile data.
   */
  async #lowestHlsVariant(masterUrl) {
    try {
      // Liquid emits protocol-relative URLs (//cdn.shopify.com/...), which are
      // not a valid base for URL resolution — absolutise before parsing.
      const master = new URL(masterUrl, window.location.href).href;
      const manifest = await fetch(master, { credentials: 'omit' }).then((r) => (r.ok ? r.text() : ''));
      const lines = manifest.split('\n');
      let best = null;

      for (let i = 0; i < lines.length; i++) {
        const match = lines[i].match(/#EXT-X-STREAM-INF:.*BANDWIDTH=(\d+)/);
        const uri = (lines[i + 1] || '').trim();
        if (!match || !uri || uri.startsWith('#')) continue;

        const bandwidth = Number(match[1]);
        if (!best || bandwidth < best.bandwidth) best = { bandwidth, uri };
      }

      return best ? new URL(best.uri, master).href : master;
    } catch {
      return masterUrl;
    }
  }

  /** Attaches a source on first play. Before this the element holds no media. */
  async #ensureSource() {
    if (this.#sourceReady) return;
    this.#sourceReady = true;

    const { hls, mp4 } = this.video.dataset;
    const canPlayHls = hls && this.video.canPlayType('application/vnd.apple.mpegurl') !== '';

    if (canPlayHls) {
      this.video.src = this.dataset.quality === 'adaptive' ? hls : await this.#lowestHlsVariant(hls);
    } else if (mp4) {
      this.video.src = mp4;
    } else {
      return;
    }

    this.video.load();
  }

  /**
   * Some browsers claim HLS support and then fail to decode it. Without a
   * <source> list there is no automatic fallback, so do it by hand.
   */
  #handleMediaError = () => {
    const { mp4 } = this.video.dataset;
    if (!mp4 || this.#triedMp4Fallback) return;

    this.#triedMp4Fallback = true;
    this.#showPoster();
    this.video.src = mp4;
    this.video.load();
    this.#play();
  };

  /** Releases the buffer — a detached element keeps downloading otherwise. */
  #unloadVideo() {
    if (!this.video) return;
    this.video.pause();
    this.video.removeAttribute('src');
    this.#sourceReady = false;
    try {
      this.video.load();
    } catch {
      /* Already torn down. */
    }
  }

  #countLoop = () => {
    const limit = Number(this.dataset.maxLoops || 0);
    if (!limit || this.isOpen || this.video.currentTime > 1) return;

    this.#loopsPlayed += 1;
    if (this.#loopsPlayed >= limit) this.video.pause();
  };

  #autoplayAllowed() {
    const connection = navigator.connection;
    if (!connection) return true;
    if (connection.saveData) return false;
    return !['slow-2g', '2g'].includes(connection.effectiveType);
  }

  /**
   * Browsers refuse to start playback while the tab is in the background, so
   * the first autoplay attempt on a restored tab is lost. Retry on the way
   * back in — unless the shopper paused it deliberately.
   */
  #handleVisibility = () => {
    if (document.visibilityState !== 'visible') return;
    if (this.#pausedByShopper || !this.video.paused) return;
    if (!this.isOpen && !this.#autoplayAllowed()) return;
    this.#play();
  };

  async #play() {
    this.#pausedByShopper = false;
    await this.#ensureSource();
    if (!this.isConnected) return;

    const attempt = this.video.play();
    if (!attempt?.catch) return;
    // Autoplay can be refused (iOS Low Power Mode, data saver). Fall back to
    // the poster plus the centre play glyph and let the shopper start it.
    attempt.catch(() => {
      this.#syncPlayState();
      this.#showPoster();
      this.#reveal();
    });
  }

  #syncPlayState = () => {
    this.dataset.playing = String(!this.video.paused);
  };

  #syncProgress = () => {
    const { currentTime, duration } = this.video;
    if (!duration || !Number.isFinite(duration)) return;
    this.style.setProperty('--mfv-progress', `${(currentTime / duration) * 100}%`);
  };

  #toggleSound = () => {
    this.video.muted = !this.video.muted;
    this.#syncSoundIcon();
  };

  #syncSoundIcon() {
    if (!this.soundButton) return;
    const muted = this.video.muted;
    this.soundButton.setAttribute('aria-pressed', String(!muted));
    const label = muted ? this.soundButton.dataset.labelOn : this.soundButton.dataset.labelOff;
    if (label) this.soundButton.setAttribute('aria-label', label);
    this.soundButton.querySelector('[data-mfv-icon-muted]')?.toggleAttribute('hidden', !muted);
    this.soundButton.querySelector('[data-mfv-icon-unmuted]')?.toggleAttribute('hidden', muted);
  }

  // ------------------------------------------------------------ geometry

  #queueViewportSync = () => {
    if (this.#resizeFrame) window.cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = window.requestAnimationFrame(() => {
      this.#resizeFrame = null;
      this.#syncViewport();
    });
  };

  /**
   * `100svh` is unreliable across the mobile browsers this store sees, and the
   * expanded frame height is derived from the tray, so both are measured here
   * and handed to CSS as custom properties.
   */
  #syncViewport() {
    this.style.setProperty('--mfv-vh', `${window.innerHeight}px`);
    if (this.tray) this.style.setProperty('--mfv-tray-h', `${Math.ceil(this.tray.offsetHeight)}px`);
    this.#syncChipDensity();
    this.#syncLift();
  }

  /**
   * The corner label and the dismiss button share the top edge of a player the
   * owner can shrink to 90px. Rather than guess a breakpoint, measure whether
   * the two would actually collide — the label text is theirs to type — and
   * fall back to the pulsing dot on its own when they would.
   */
  #syncChipDensity() {
    if (!this.chip) return;

    if (!this.chip.querySelector('[data-mfv-chip-label]')) {
      this.dataset.compact = 'true';
      return;
    }

    if (this.isOpen) return;

    this.dataset.compact = 'false';
    const label = this.chip.getBoundingClientRect();
    const dismiss = this.dismissButton?.getBoundingClientRect();
    const limit = dismiss ? dismiss.left : this.frame.getBoundingClientRect().right - 6;

    if (label.right > limit - 4) this.dataset.compact = 'true';
  }

  /**
   * Keeps the mini player clear of whatever else lives in the bottom corners:
   * the theme's own sticky add-to-cart bar, and the store's WhatsApp bubble —
   * which matters as soon as the owner moves the player to the right.
   */
  #syncLift() {
    const clearances = [0];

    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (bar instanceof HTMLElement && bar.dataset.stuck === 'true') {
      clearances.push(Math.ceil(bar.getBoundingClientRect().height) + 28);
    }

    const chat = document.querySelector('.wa__btn_popup, #shopify-chat, .shopify-chat-container');
    if (chat && !this.isOpen) {
      const chatBox = chat.getBoundingClientRect();
      const frame = this.frame.getBoundingClientRect();
      const sharesTheCorner = chatBox.height > 0 && chatBox.left < frame.right && chatBox.right > frame.left;

      if (sharesTheCorner) clearances.push(Math.ceil(window.innerHeight - chatBox.top) + 12);
    }

    this.style.setProperty('--mfv-lift', `${Math.max(...clearances)}px`);
  }

  #watchStickyBar() {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar) return;
    this.#stickyObserver = new MutationObserver(() => this.#syncLift());
    this.#stickyObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  }

  #readLength(name, fallback) {
    const value = parseFloat(window.getComputedStyle(this).getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  }

  // ---------------------------------------------------------- open / close

  get isOpen() {
    return this.dataset.open === 'true';
  }

  #handleSurface = () => {
    if (!this.isOpen) {
      this.open();
      return;
    }
    if (this.video.paused) {
      this.#play();
    } else {
      this.#pausedByShopper = true;
      this.video.pause();
    }
  };

  open() {
    if (this.isOpen) return;

    this.#syncViewport();
    const first = this.frame.getBoundingClientRect();
    this.#morph?.cancel();

    this.dataset.open = 'true';
    this.#lockScroll();

    const last = this.frame.getBoundingClientRect();
    this.#runMorph(first, last, this.#readLength('--mfv-radius', 14), this.#readLength('--mfv-open-radius', 4));

    // A deliberate expand earns a fresh loop budget.
    this.#loopsPlayed = 0;

    // Expanding is a user gesture, so sound is allowed to come on here.
    if (this.dataset.unmute === 'true') this.video.muted = false;
    this.#syncSoundIcon();
    this.#play();
    this.closeButton?.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;

    const first = this.frame.getBoundingClientRect();
    this.#morph?.cancel();

    this.dataset.open = 'false';
    this.#unlockScroll();

    const last = this.frame.getBoundingClientRect();
    this.#runMorph(first, last, this.#readLength('--mfv-open-radius', 4), this.#readLength('--mfv-radius', 14));

    this.video.muted = true;
    this.#syncSoundIcon();
    this.#play();
  }

  /**
   * @param {DOMRect} first - Where the frame visually is right now.
   * @param {DOMRect} last - Where the new layout has just put it.
   * @param {number} fromRadius - Corner radius that should *appear* at the start.
   * @param {number} toRadius - Corner radius the CSS end state settles on.
   */
  #runMorph(first, last, fromRadius, toRadius) {
    if (prefersReducedMotion()) return;

    const scale = first.width / last.width;
    if (!Number.isFinite(scale) || scale <= 0) return;

    this.#morph = this.frame.animate(
      [
        {
          transform: `translate(${first.left - last.left}px, ${first.top - last.top}px) scale(${scale})`,
          // Radius is scaled along with the box, so pre-divide to keep it visually constant.
          borderRadius: `${fromRadius / scale}px`,
        },
        { transform: 'translate(0px, 0px) scale(1)', borderRadius: `${toRadius}px` },
      ],
      { duration: Number(this.dataset.duration || 480), easing: EASING }
    );

    this.#morph.addEventListener('finish', () => {
      this.#morph = null;
    });
  }

  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  #lockScroll() {
    // The class also parks third-party chat bubbles for the duration; see the
    // html.mfv-open rules in mala-floating-video.css.
    document.documentElement.classList.add('mfv-open');
    if (this.#scrollLocked) return;
    this.#scrollLocked = true;
    this.#previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  #unlockScroll() {
    document.documentElement.classList.remove('mfv-open');
    if (!this.#scrollLocked) return;
    this.#scrollLocked = false;
    document.documentElement.style.overflow = this.#previousOverflow;
  }

  // -------------------------------------------------------------- add to cart

  #productForm() {
    return document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  /**
   * Mirrors the real button's label and disabled state, so "Sold out" and
   * quantity-rule states show up in the tray without duplicating any logic.
   */
  #watchProductForm() {
    const form = this.#productForm();
    if (!form) return;

    this.#formObserver = new MutationObserver(() => this.#syncButton());
    this.#formObserver.observe(form, { childList: true, subtree: true, attributes: true });
    this.#syncButton();
  }

  #syncButton() {
    if (!this.atcButton) return;

    this.#realButton = this.#productForm()?.querySelector('[ref="addToCartButton"]') ?? null;
    if (!this.#realButton) return;

    const label = this.#realButton.textContent?.replace(/\s+/g, ' ').trim();
    if (label) this.atcButton.textContent = label;
    this.atcButton.disabled = this.#realButton.disabled;
  }

  #addToCart = () => {
    this.#syncButton();
    const button = this.#realButton;

    // Close first: the cart drawer opens on top, and a fly-to-cart animation
    // starting from an off-screen button would look broken.
    this.close();

    if (!button) {
      if (this.dataset.productUrl) window.location.assign(this.dataset.productUrl);
      return;
    }

    button.dataset.puppet = 'true';
    button.click();
    window.setTimeout(() => {
      button.dataset.puppet = 'false';
    }, 2000);
  };
}

if (!customElements.get('mala-floating-video')) {
  customElements.define('mala-floating-video', MalaFloatingVideo);
}
